import { Component, Input, OnInit } from '@angular/core';

/**
 * A reusable pie chart SVG component that displays a percentage as a circular progress indicator.
 *
 * @example
 * ```html
 * <gains-pie-svg
 *   [percentage]="85"
 *   [label]="'Current Service Level'"
 *   [color]="'#7ba428'"
 *   [size]="60">
 * </gains-pie-svg>
 * ```
 */
@Component({
  selector: 'gains-pie-svg',
  standalone: false,
  templateUrl: './gains-pie-svg.component.html',
  styleUrl: './gains-pie-svg.component.scss'
})
export class GainsPieSvgComponent implements OnInit {
  /** The percentage value to display (0-100) */
  @Input() percentage = 0;

  /** The label text to display below the percentage */
  @Input() label = '';

  /** The stroke color for the pie chart segment (hex or rgba) */
  @Input() color = '#7ba428';

  /** The size of the SVG in pixels */
  @Input() size = 45;

  // Constants
  private readonly SVG_RADIUS = 50;
  private readonly MIN_PERCENTAGE = 0;
  private readonly MAX_PERCENTAGE = 100;

  /** Validated and clamped percentage value */
  validatedPercentage = 0;

  ngOnInit(): void {
    this.validatePercentage();
  }

  /**
   * Validates and clamps the percentage value to be between 0 and 100.
   * @private
   */
  private validatePercentage(): void {
    this.validatedPercentage = Math.max(this.MIN_PERCENTAGE, Math.min(this.MAX_PERCENTAGE, this.percentage));

    if (this.percentage !== this.validatedPercentage) {
      console.warn(`GainsPieSvgComponent: Percentage value ${this.percentage} is out of range. ` + `Clamped to ${this.validatedPercentage}.`);
    }
  }

  /**
   * Calculates the SVG stroke-dasharray value for the pie chart based on percentage.
   * @param percentage The percentage value (0-100)
   * @returns The dasharray string in format "fillLength circumference"
   */
  getPieDasharray(percentage: number): string {
    const circumference = 2 * Math.PI * this.SVG_RADIUS;
    const fillLength = (percentage / 100) * circumference;
    return `${fillLength} ${circumference}`;
  }

  /**
   * Gets the accessible label for screen readers.
   * @returns Descriptive text for accessibility
   */
  getAriaLabel(): string {
    return `${this.label}: ${this.validatedPercentage} percent`;
  }
}
